/**
 * Effective permissions for the logged-in user (from their permission group).
 * See permissionStorage.js for cache helpers; this module adds API refresh only.
 */

import {
  applyUserPermissionsPayload,
  getLoggedInUserId,
  PERMISSIONS_REFRESH_REQUEST_EVENT,
} from "./permissionStorage";
import {
  applyPermissionGroupsToUser,
  normalizePermissionGroupIds,
} from "./permissionGroupMembership";
import { isPermissionsSuperAdmin } from "./superAdmin";

export {
  PERMISSIONS_STORAGE_KEY,
  PERMISSION_GROUP_ID_KEY,
  PERMISSION_GROUP_IDS_KEY,
  PERMISSION_GROUP_NAME_KEY,
  PERMISSION_GROUPS_KEY,
  PERMISSIONS_SOURCE_KEY,
  PERMISSIONS_UPDATED_EVENT,
  isPermissionEnabled,
  normalizePermissionsPayload,
  applyUserPermissionsPayload,
  getPermissions,
  hasPermission,
  getPermissionGroupMeta,
  getLoggedInUserId,
  hasAssignedPermissionGroup,
  currentUserBelongsToPermissionGroup,
  PERMISSIONS_REFRESH_REQUEST_EVENT,
} from "./permissionStorage";

export { isPermissionsSuperAdmin, PERMISSIONS_SUPER_ADMIN_EMAIL } from "./superAdmin";

function syncSessionUserFromPermissionApi(data) {
  if (!data || typeof data !== "object") return;

  try {
    const raw = localStorage.getItem("user");
    if (!raw) return;
    const user = JSON.parse(raw);
    if (!user || typeof user !== "object") return;

    const next = applyPermissionGroupsToUser(user, data);
    localStorage.setItem("user", JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function requestPermissionsRefresh() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(PERMISSIONS_REFRESH_REQUEST_EVENT));
  }
}

function mergeSuperAdminPermissionsFromApi(data) {
  if (!data || typeof data !== "object") return data;
  const hasGroup =
    (Array.isArray(data.permission_group_ids) &&
      data.permission_group_ids.length > 0) ||
    (data.permission_group_id != null &&
      data.permission_group_id !== "" &&
      Number(data.permission_group_id) !== 0);
  if (hasGroup || !isPermissionsSuperAdmin()) {
    return data;
  }
  const perms = { ...(data.permissions || {}) };
  perms.permissions_page = 1;
  return {
    ...data,
    permissions: perms,
    source: data.source || "super_admin",
  };
}

/**
 * Reload effective permissions from the API (user's role assignments).
 */
/** Load effective permissions payload for any user (admin / account screens). */
export async function fetchUserPermissionsForUser(userId) {
  if (!userId) return null;
  const { apiUrl } = await import("../components/ApiManager/AxiosHeaders");
  const res = await apiUrl.get(`getUserPermissions/${userId}`);
  const body = res.data;
  return body?.data ?? body;
}

let refreshCurrentUserPermissionsInflight = null;

export async function refreshCurrentUserPermissions() {
  const userId = getLoggedInUserId();
  if (!userId || !localStorage.getItem("token")) {
    return null;
  }

  if (refreshCurrentUserPermissionsInflight) {
    return refreshCurrentUserPermissionsInflight;
  }

  refreshCurrentUserPermissionsInflight = (async () => {
    try {
      const data = await fetchUserPermissionsForUser(userId);
      if (!data || typeof data !== "object") return null;

      const merged = mergeSuperAdminPermissionsFromApi(data);
      syncSessionUserFromPermissionApi(merged);

      return applyUserPermissionsPayload(merged);
    } catch (e) {
      console.error("refreshCurrentUserPermissions failed", e);
      return null;
    } finally {
      refreshCurrentUserPermissionsInflight = null;
    }
  })();

  return refreshCurrentUserPermissionsInflight;
}
